import React, { useState, useEffect } from "react";

// --- Reusable Counter Component for Animation ---
const Counter = ({ end, duration = 2000 }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime;
    let animationFrame;

    const animateCount = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = timestamp - startTime;

      // Calculate current value based on progress
      const percentage = Math.min(progress / duration, 1);
      const currentCount = Math.floor(percentage * end);

      setCount(currentCount);

      if (progress < duration) {
        animationFrame = requestAnimationFrame(animateCount);
      }
    };

    animationFrame = requestAnimationFrame(animateCount);

    // Cleanup on unmount
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration]);

  return <span>{count}+</span>;
};

const Stats = () => {
  return (
    // Section Container: Blue Gradient Background
    <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-800 text-white relative overflow-hidden">
      {/* Decorative background circles (subtle texture) */}
      <div className="absolute top-0 right-0 -mr-20 -mt-20 w-80 h-80 rounded-full bg-white opacity-5 blur-3xl"></div>
      <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-80 h-80 rounded-full bg-blue-300 opacity-10 blur-3xl"></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Grid Layout: 2 columns on mobile, 4 on desktop */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 lg:gap-12 text-center">
          {/* Stat Item 1: Projects */}
          <div className="flex flex-col items-center">
            <div className="mb-4 p-4 bg-blue-500/30 rounded-full backdrop-blur-sm border border-blue-400/20">
              <svg
                className="w-8 h-8 text-blue-100"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
            </div>
            <h3 className="text-4xl md:text-5xl font-extrabold mb-2 tracking-tight">
              <Counter end={10} duration={2500} />
            </h3>
            <p className="text-blue-100 font-medium text-sm md:text-base uppercase tracking-wider">
              Projects Completed
            </p>
          </div>

          {/* Stat Item 2: Clients */}
          <div className="flex flex-col items-center">
            <div className="mb-4 p-4 bg-blue-500/30 rounded-full backdrop-blur-sm border border-blue-400/20">
              <svg
                className="w-8 h-8 text-blue-100"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
            <h3 className="text-4xl md:text-5xl font-exparabol mb-2 tracking-tight">
              <Counter end={20} duration={2000} />
            </h3>
            <p className="text-blue-100 font-medium text-sm md:text-base uppercase tracking-wider">
              Gov. Agencies
            </p>
          </div>

          {/* Stat Item 3: Years */}
          <div className="flex flex-col items-center">
            <div className="mb-4 p-4 bg-blue-500/30 rounded-full backdrop-blur-sm border border-blue-400/20">
              <svg
                className="w-8 h-8 text-blue-100"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="text-4xl md:text-5xl font-extrabold mb-2 tracking-tight">
              <Counter end={10} duration={1500} />
            </h3>
            <p className="text-blue-100 font-medium text-sm md:text-base uppercase tracking-wider">
              Years Experience
            </p>
          </div>

          {/* Stat Item 4: Employees */}
          <div className="flex flex-col items-center">
            <div className="mb-4 p-4 bg-blue-500/30 rounded-full backdrop-blur-sm border border-blue-400/20">
              <svg
                className="w-8 h-8 text-blue-100"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                />
              </svg>
            </div>
            <h3 className="text-4xl md:text-5xl font-extrabold mb-2 tracking-tight">
              <Counter end={35} duration={2200} />
            </h3>
            <p className="text-blue-100 font-medium text-sm md:text-base uppercase tracking-wider">
              Expert Engineers
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Stats;
